"use client";

import { useState } from "react";
import { Folder, FolderOpen } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ProcessDefinition } from "@/lib/camunda";
import { ProcessItem } from "@/components/process-item";
import { getCategoryState, setCategoryState } from "@/lib/user-preferences";

interface ProcessCategoryProps {
  category: string;
  processes: ProcessDefinition[];
  startingProcess: string | null;
  onStartProcess: (process: ProcessDefinition) => void;
  defaultOpen?: boolean;
}

export function ProcessCategory({
  category,
  processes,
  startingProcess,
  onStartProcess,
  defaultOpen = false
}: ProcessCategoryProps) {
  const [isOpen, setIsOpen] = useState(() => {
    const savedState = getCategoryState(category);
    return savedState !== null ? savedState : defaultOpen;
  });

  const handleValueChange = (value: string) => {
    const newIsOpen = value === category;
    setIsOpen(newIsOpen);
    setCategoryState(category, newIsOpen);
  };

  return (
    <Accordion 
      type="single" 
      collapsible
      value={isOpen ? category : ""}
      onValueChange={handleValueChange}
      className="border border-gray-200 rounded-lg overflow-hidden bg-white"
    >
      <AccordionItem value={category} className="border-none">
        <AccordionTrigger className="hover:bg-gray-50 transition-colors duration-200 p-4 hover:no-underline group">
          <div className="flex items-center space-x-3">
            <div className="transition-all duration-200">
              <Folder className="h-5 w-5 text-gray-600 group-data-[state=open]:hidden" />
              <FolderOpen className="h-5 w-5 text-blue-600 hidden group-data-[state=open]:block" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">{category}</h2>
            <Badge variant="secondary" className="bg-gray-100 text-gray-700">
              {processes.length}
            </Badge>
          </div>
        </AccordionTrigger>
        
        <AccordionContent className="px-4 pb-4">
          <div className="space-y-0">
            {processes.map((process) => (
              <ProcessItem
                key={process.id}
                process={process}
                isStarting={startingProcess === process.key}
                onStart={() => onStartProcess(process)}
              />
            ))}
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}